import React, { useRef } from 'react'

import {
  buttonIconStyles,
  fieldStyles,
  inputStyles,
  labelStyles,
  ornamentStyles,
} from './styles'
import { useAutosizeTextArea } from './useAutosizeTextArea'
import { Icon, IconSize } from '@sidecar-components/atoms/Icon'
import { InputHelperText } from '@sidecar-components/atoms/InputHelperText'
import { TextAreaProps } from './TextArea.types'

export const TextArea = ({
  button,
  buttonSize,
  className,
  testId,
  disabled,
  error,
  helperText,
  inputClass,
  label,
  name,
  onBlur,
  onButtonClick,
  onChange,
  onFocus,
  placeholder,
  rows = 2,
  value,
}: TextAreaProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  useAutosizeTextArea(textAreaRef.current, value)

  return (
    <div className={className}>
      <div className={fieldStyles}>
        {/* we're using ternary with null here because false is not a valid
            aria value. Setting to null means the value won't render */}
        <textarea
          ref={textAreaRef}
          aria-describedby={helperText ? `${name}-helper` : null}
          aria-disabled={disabled}
          aria-invalid={!!error}
          aria-labelledby={name ? `${name}-label` : null}
          className={`${inputStyles({
            button,
            disabled,
            error,
            label,
          })} ${inputClass}`}
          data-testid={testId}
          disabled={disabled}
          id={name}
          name={name}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          rows={rows}
          value={value}
          /*The Math.max is to avoid the possibility of a bad rows value
          being passed, resulting in negative numbers and a reduced height
          that will break the styling entirely.*/
          style={{ minHeight: `${64 + Math.max(rows - 1, 0) * 24}px` }}
        />

        {/* The label acts kind of like a placeholder, except it moves out of
            the way. It's underneath the input so that we can use peer-* 
            classes to style things */}
        {!!label && (
          <label
            htmlFor={name}
            id={`${name}-label`}
            className={labelStyles({ error, value })}
          >
            {label}
          </label>
        )}

        {/* Buttons are like an 'x' you use to clear a field, on right. */}
        {!!button && (
          <button
            onClick={onButtonClick}
            className={`${ornamentStyles} right-4 group/button focus:outline-none`}
          >
            <Icon
              name={button}
              size={buttonSize ? buttonSize : IconSize.MD}
              className={buttonIconStyles}
            />
          </button>
        )}
      </div>

      {/* The error and helper text will be separate from the input just so
          we keep our sanity when trying to position everything. */}
      <InputHelperText name={name} error={error} helperText={helperText} />
    </div>
  )
}
